import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { Lock, ArrowRight, Database, Wrench, BookOpen, Download, CalendarClock, Trash2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ModuleLockedModal from "../components/ModuleLockedModal";

function GatedGrid({ items, icon: Icon, kindLabel, linkPrefix }) {
  // A locked module can be in one of four states:
  //   1. tier lock (free user, paid content) → CTA routes to
  //      /account/subscription (Subscription & Billing page)
  //   2. plan_gated (Menu Directory admin config → this user's plan is
  //      not on the module's `plans` allow-list) → same "Upgrade to
  //      Unlock" CTA + /account/subscription route as tier lock
  //   3. sequential_core (paid user, previous core not done) → click
  //      opens a small modal explaining the gate and offers a "Go to
  //      Module X" button that jumps to the immediately previous
  //      incomplete module.
  //   4. bonus_gated (paid user, cores not all done) → click opens the
  //      "Bonus Modules Locked" modal.
  const [gate, setGate] = React.useState(null);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it) => {
        const reason = it.locked_reason;
        const seqLock = reason?.kind === "sequential_core";
        const bonusLock = reason?.kind === "bonus_gated";
        const planLock = reason?.kind === "plan_gated";
        const tierLock = it.locked && !seqLock && !bonusLock && !planLock;
        // Both tier-lock and Menu-Directory plan-gate route to the same
        // upgrade destination — the copy is identical from the user's
        // perspective ("Upgrade to Unlock") so we treat them the same.
        const upgradeLock = tierLock || planLock;
        const inner = (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-xl bg-primary/10 grid place-items-center">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              {it.locked && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-secondary text-white px-2 py-1 rounded-full">
                  <Lock className="w-3 h-3" />
                  {seqLock ? `finish M${reason.unlock_after_number}`
                    : bonusLock ? "cores first"
                    : "upgrade"}
                </span>
              )}
            </div>
            <h3 className="font-display text-2xl mt-4">{it.name}</h3>
            <p className="text-sm text-muted-foreground mt-2 flex-1">{it.description}</p>
            <span className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-primary">
              {upgradeLock ? "Upgrade to unlock"
                : seqLock ? `Complete Module ${reason.unlock_after_number} to unlock`
                : bonusLock ? "Finish the 10 core modules to unlock"
                : "Open"}
              {" "}
              <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        );
        const cls = [
          "card-elevated p-6",
          it.locked ? "opacity-80" : "hover:-translate-y-0.5 transition-transform",
        ].join(" ");
        if (upgradeLock) {
          return (
            <Link key={it.slug} to="/account/subscription" className={cls} data-testid={`${kindLabel}-card-${it.slug}`}>
              {inner}
            </Link>
          );
        }
        if (seqLock || bonusLock) {
          return (
            <button
              key={it.slug}
              type="button"
              onClick={() => setGate({ ...reason, blockedName: it.name, blockedNumber: reason.current_number })}
              className={cls + " text-left w-full"}
              data-testid={`${kindLabel}-card-${it.slug}`}
            >
              {inner}
            </button>
          );
        }
        return (
          <Link key={it.slug} to={`${linkPrefix}/${it.slug}`} className={cls} data-testid={`${kindLabel}-card-${it.slug}`}>
            {inner}
          </Link>
        );
      })}
      {gate && <ModuleLockedModal gate={gate} onClose={() => setGate(null)} linkPrefix={linkPrefix} />}
    </div>
  );
}

function useList(path) {
  const [data, setData] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get(path)
        .then((r) => { if (!cancelled) setData(r.data); })
        .catch(() => {});
    };
    load();
    // Refetch whenever another route publishes a module-progress event
    // (e.g. quiz pass in ModuleQuiz.jsx). Without this, cards on this
    // page keep their stale `locked` state until a hard refresh.
    const onProgress = () => load();
    window.addEventListener("cpg:module-progress", onProgress);
    return () => {
      cancelled = true;
      window.removeEventListener("cpg:module-progress", onProgress);
    };
  }, [path]);
  return data;
}

export function Databases() {
  const data = useList("/databases");
  return (
    <AppShell>
      <PageHeader eyebrow="Directories" title="7 vetted databases for CPG operators"
        subtitle="Retailers, distributors, brokers, co-packers, agencies, investors and trade media." />
      <GatedGrid items={data} icon={Database} kindLabel="database" linkPrefix="/databases" />
    </AppShell>
  );
}

export function Tools() {
  const data = useList("/tools");
  return (
    <AppShell>
      <PageHeader eyebrow="Tools" title="Purpose-built for CPG P&Ls"
        subtitle="Retail readiness, trade planning, media mix — calibrated to real CPG margins." />
      <GatedGrid items={data} icon={Wrench} kindLabel="tool" linkPrefix="/tools" />
    </AppShell>
  );
}

export function Modules() {
  const data = useList("/modules");
  const core = data.filter((m) => m.track === "core");
  const bonus = data.filter((m) => m.track === "bonus");
  return (
    <AppShell>
      <PageHeader eyebrow="Curriculum" title="Modules & Worksheets"
        subtitle="10 core modules toward the Certified CPG Founder™ badge, plus 6 bonus specialist modules." />
      <div className="mb-4 flex items-center gap-4">
        <p className="text-xs uppercase tracking-widest text-shell-side font-semibold">Core modules</p>
        <p className="text-xs text-muted-foreground">{core.length} required for primary certification</p>
      </div>
      <GatedGrid items={core} icon={BookOpen} kindLabel="module" linkPrefix="/modules" />
      <div className="mt-12 mb-4 flex items-center gap-4">
        <p className="text-xs uppercase tracking-widest text-shell-side font-semibold">Bonus modules · specialist certifications</p>
        <p className="text-xs text-muted-foreground">{bonus.length} additional badges</p>
      </div>
      <GatedGrid items={bonus} icon={BookOpen} kindLabel="module" linkPrefix="/modules" />
    </AppShell>
  );
}

export function Downloads() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/downloads").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const download = async (id, filename) => {
    try {
      const res = await api.get(`/downloads/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "download"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <AppShell>
      <PageHeader eyebrow="Templates" title="Downloadable library"
        subtitle="Files, templates, and datasets curated by the CPG Institute team." />
      {items.length === 0 ? (
        <EmptyState title="No downloads yet" subtitle="An admin can upload PDFs, CSVs and templates from /admin. They'll appear here." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`download-card-${it.id}`} className="card-elevated p-6 flex flex-col">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-primary">
                  <Download className="w-4 h-4" /> {it.ext?.toUpperCase()}
                </span>
                {it.locked && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-secondary text-white px-2 py-1 rounded-full">
                    <Lock className="w-3 h-3" /> Locked
                  </span>
                )}
              </div>
              <h3 className="font-display text-xl mt-4">{it.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 flex-1">{it.description}</p>
              {it.locked ? (
                <Link to="/account/subscription" className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid={`download-upgrade-${it.id}`}>
                  Upgrade to unlock <ArrowRight className="w-3 h-3" />
                </Link>
              ) : (
                <button onClick={() => download(it.id, it.original_filename)} className="btn-primary mt-5" data-testid={`download-button-${it.id}`}>
                  Download
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

export function Planner() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ title: "", channel: "Retail", month: "Jan", budget: 0, notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/planner").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setBusy(true);
    try {
      await api.post("/planner", { ...draft, budget: Number(draft.budget) || 0 });
      setDraft({ title: "", channel: "Retail", month: "Jan", budget: 0, notes: "" });
      await load();
      toast.success("Added");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/planner/${id}`); await load(); } catch (e) { toast.error(formatError(e)); } };

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const channels = ["Retail","Trade","Paid","Owned","Earned","Sampling","Events"];

  return (
    <AppShell>
      <PageHeader eyebrow="Marketing Planner" title="Plan by channel and month"
        subtitle="A simple, structured workspace for your yearly marketing calendar." />
      <form onSubmit={add} className="grid md:grid-cols-6 gap-3 card-elevated p-5">
        <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Initiative name" data-testid="planner-title"
          className="md:col-span-2 rounded-xl bg-white border border-border px-3 py-2 text-sm" />
        <select value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
          data-testid="planner-channel" className="rounded-xl bg-white border border-border px-3 py-2 text-sm">
          {channels.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={draft.month} onChange={(e) => setDraft({ ...draft, month: e.target.value })}
          data-testid="planner-month" className="rounded-xl bg-white border border-border px-3 py-2 text-sm">
          {months.map((m) => <option key={m}>{m}</option>)}
        </select>
        <input value={draft.budget} onChange={(e) => setDraft({ ...draft, budget: e.target.value })}
          placeholder="Budget" type="number" data-testid="planner-budget"
          className="rounded-xl bg-white border border-border px-3 py-2 text-sm" />
        <button type="submit" disabled={busy} data-testid="planner-add"
          className="btn-primary justify-center">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      <div className="mt-8">
        {items.length === 0 ? (
          <EmptyState title="Your plan is empty" subtitle="Add your first initiative above." />
        ) : (
          <div className="card-elevated overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-xs [&>th]:tracking-widest">
                  <th>Initiative</th><th>Channel</th><th>Month</th>
                  <th className="text-right">Budget</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`planner-row-${it.id}`} className="border-t border-border">
                    <td className="px-4 py-3 font-semibold">{it.title}</td>
                    <td className="px-4 py-3">{it.channel}</td>
                    <td className="px-4 py-3">{it.month}</td>
                    <td className="px-4 py-3 text-right">${Number(it.budget || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(it.id)} data-testid={`planner-delete-${it.id}`} className="p-2 hover:bg-muted rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <PageHero
      eyebrow={eyebrow}
      icon={Sparkles}
      title={title}
      subtitle={subtitle}
    />
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div className="border border-dashed border-border p-16 text-center rounded-2xl bg-white">
      <p className="font-display text-3xl">{title}</p>
      <p className="text-sm text-muted-foreground mt-2">{subtitle}</p>
    </div>
  );
}
